import styled from 'styled-components';

import { Button } from 'shared/components/Button';
import { CountdownComponent as Countdown } from 'shared/components/Countdown';
import { variableFontRegular } from 'styles/theme/text';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { useCurrency } from 'shared/hooks/useCurrency';
import { t } from 'localization/utils/t';

const TP = 'productTemplate.components.buyBar.OfferSection.MobileOfferSectionListItem';

interface IMobileOfferSectionListItem {
  topOfferPrice: number;
  userOfferPrice?: number;
  lastSalePrice?: number;
  isSneaker: boolean;
  handleOnClick: () => void;
  remainingOfferDuration?: any;
  isLoading?: boolean;
}

export const MobileOfferSectionListItem = ({
  handleOnClick,
  topOfferPrice,
  userOfferPrice,
  lastSalePrice,
  isSneaker,
  remainingOfferDuration,
  isLoading,
}: IMobileOfferSectionListItem) => {
  const { selectedCurrencyCode } = useCurrency();

  const userHasOffer = Boolean(userOfferPrice);
  const userHasTopOffer = typeof userOfferPrice === 'number' && userOfferPrice === topOfferPrice;
  const yourOfferCopy = t(`${TP}.yourOffer`, 'Your Offer');
  const topOfferCopy = t(`${TP}.topOffer`, 'Top Offer');
  const conditionCopy = isSneaker ? t(`${TP}.newInBox`, 'New in Box') : t(`${TP}.new`, 'New');
  const lastSaleCopy = t(`${TP}.lastSale`, 'Last Sale');
  const buttonCopy = userHasOffer ? t(`${TP}.edit`, 'Edit') : t(`${TP}.offer`, 'Offer');
  const setThePriceCopy =
    selectedCurrencyCode === 'USD'
      ? t(`${TP}.setThePrice`, 'Set the price you’re willing to pay.')
      : t(
          `${TP}.setThePriceUsd`,
          'Set the price you’re willing to pay. Offers will transact in USD',
        );
  const haveHighestOfferCopy = t(`${TP}.haveHighestOffer`, 'You have the highest offer');
  const displayTopOfferCopy = userHasTopOffer ? yourOfferCopy : topOfferCopy;

  const messageCopy =
    (!userHasOffer && setThePriceCopy) || (userHasTopOffer && haveHighestOfferCopy);

  const topOfferValue = topOfferPrice ? (
    <LocalizedCurrency price={topOfferPrice} data-qa="top_offer_price" />
  ) : (
    <MobileOfferSectionListItem.Line />
  );
  const lastSaleValue = lastSalePrice ? (
    <LocalizedCurrency price={lastSalePrice} data-qa="last_sold_price" />
  ) : (
    <MobileOfferSectionListItem.Line />
  );

  return (
    <MobileOfferSectionListItem.Root>
      {userHasOffer && !userHasTopOffer && (
        <MobileOfferSectionListItem.OffersCopy>
          {yourOfferCopy}
        </MobileOfferSectionListItem.OffersCopy>
      )}
      {userOfferPrice && !userHasTopOffer && (
        <MobileOfferSectionListItem.Pricing>
          <LocalizedCurrency price={userOfferPrice} data-qa="user_offer_price" />
        </MobileOfferSectionListItem.Pricing>
      )}
      <MobileOfferSectionListItem.Button
        onClick={handleOnClick}
        data-qa="make_offer_button"
        loading={isLoading}
      >
        {buttonCopy}
      </MobileOfferSectionListItem.Button>
      <MobileOfferSectionListItem.OffersCopy data-qa="mobile_offer_copy">
        {displayTopOfferCopy}
      </MobileOfferSectionListItem.OffersCopy>
      <MobileOfferSectionListItem.Pricing data-qa="mobile_offer_list_item_pricing">
        {topOfferValue}
      </MobileOfferSectionListItem.Pricing>
      <MobileOfferSectionListItem.OffersCopy data-qa="mobile_offer_list_item_last_sale">
        {lastSaleCopy}
      </MobileOfferSectionListItem.OffersCopy>
      <MobileOfferSectionListItem.Pricing>{lastSaleValue}</MobileOfferSectionListItem.Pricing>
      <MobileOfferSectionListItem.MoreInfo>
        <div>
          <MobileOfferSectionListItem.Condition data-qa="mobile_offer_list_item_condition_copy">
            {conditionCopy}
          </MobileOfferSectionListItem.Condition>
          {messageCopy && (
            <MobileOfferSectionListItem.Message data-qa="offer_section_list_item_message_copy">
              {messageCopy}
            </MobileOfferSectionListItem.Message>
          )}
        </div>
        {remainingOfferDuration && (
          <MobileOfferSectionListItem.Countdown
            active
            date={remainingOfferDuration}
            hoursOnly={false}
            isLowercase
          />
        )}
      </MobileOfferSectionListItem.MoreInfo>
    </MobileOfferSectionListItem.Root>
  );
};

MobileOfferSectionListItem.Root = styled.div`
  ${variableFontRegular({ height: '11.5px', size: '11.5px' })};
  margin-top: 32px;
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  column-gap: 16px;
  row-gap: 8px;
`;

MobileOfferSectionListItem.Line = styled.span`
  ${variableFontRegular({ size: '13px', height: '13px' })};
  text-align: center;
  min-width: 18px;
  font-weight: 400;
  &:before {
    content: '----';
  }
`;

MobileOfferSectionListItem.OffersCopy = styled.div`
  ${variableFontRegular({ size: '13px', height: '13px' })}
  grid-column: 1;
`;

MobileOfferSectionListItem.Pricing = styled.div`
  grid-column: 2;
`;

MobileOfferSectionListItem.Button = styled(Button)`
  grid-column: 3;
  grid-row: 1 / span 2;
  width: auto;
  height: 20px;
  font-size: 11px;
  padding: 3px;
  margin-left: auto;
  white-space: nowrap;
  text-transform: capitalize;
  border: ${({ theme }) => theme.borders.opaqueWhite};

  ${Button.LoaderWrapper} {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
    padding: 16px 0;
  }

  ${Button.Loader} {
    min-width: 32px;
  }
`;

MobileOfferSectionListItem.MoreInfo = styled.div`
  grid-column: 1 / -1;

  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr;
  flex-direction: column;

  margin-top: 16px;
`;

MobileOfferSectionListItem.Condition = styled.div`
  ${variableFontRegular({ size: '11px', height: '11px' })}
`;

MobileOfferSectionListItem.Message = styled.div`
  margin-top: 6px;
  grid-column: 1;
  ${variableFontRegular({ size: '9px', height: '12px' })}
`;

MobileOfferSectionListItem.Countdown = styled(Countdown)`
  ${variableFontRegular({ size: '9px', height: '12px' })}
  color: ${({ theme }) => theme.colors.white};
  grid-column: 2;
  text-align: right;
  align-self: end;
`;
